import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // Dialog states
  candidateDetailDialog: {
    open: false,
    candidate: null,
  },
  
  // View preferences
  viewMode: 'table', // 'table', 'grid', 'list'
  
  // Theme preferences
  theme: 'light', // 'light', 'dark'
  
  // Notifications
  notifications: [],
  
  // Loading states
  globalLoading: false,
  
  // Sidebar state
  sidebarOpen: false,
  sidebarExpanded: true,
  isMobile: false,
  
  // Search and filters
  searchQuery: '',
  activeFilters: {},
  
  // Pagination
  currentPage: 1,
  itemsPerPage: 10,
  
  // Sorting
  sortBy: 'name',
  sortOrder: 'asc',
  
  // Privacy/Hide mode
  hideMode: false,
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    // Dialog actions
    openCandidateDetail: (state, action) => {
      state.candidateDetailDialog.open = true;
      state.candidateDetailDialog.candidate = action.payload;
    },
    closeCandidateDetail: (state) => {
      state.candidateDetailDialog.open = false;
      state.candidateDetailDialog.candidate = null;
    },
    
    // View mode actions
    setViewMode: (state, action) => {
      state.viewMode = action.payload;
    },
    
    // Theme actions
    toggleTheme: (state) => {
      state.theme = state.theme === 'light' ? 'dark' : 'light';
    },
    setTheme: (state, action) => {
      state.theme = action.payload;
    },
    
    // Notification actions
    addNotification: (state, action) => {
      state.notifications.push({
        id: Date.now(),
        ...action.payload,
      });
    },
    removeNotification: (state, action) => {
      state.notifications = state.notifications.filter(
        notification => notification.id !== action.payload
      );
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
    
    // Loading actions
    setGlobalLoading: (state, action) => {
      state.globalLoading = action.payload;
    },
    
    // Sidebar actions
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    openSidebar: (state) => {
      state.sidebarOpen = true;
    },
    closeSidebar: (state) => {
      state.sidebarOpen = false;
    },
    setSidebarOpen: (state, action) => {
      state.sidebarOpen = action.payload;
    },
    toggleSidebarExpanded: (state) => {
      state.sidebarExpanded = !state.sidebarExpanded;
    },
    setSidebarExpanded: (state, action) => {
      state.sidebarExpanded = action.payload;
    },
    setMobileView: (state, action) => {
      state.isMobile = action.payload;
    },
    
    // Search and filter actions
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    setActiveFilters: (state, action) => {
      state.activeFilters = action.payload;
    },
    clearFilters: (state) => {
      state.activeFilters = {};
      state.searchQuery = '';
    },
    
    // Pagination actions
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setItemsPerPage: (state, action) => {
      state.itemsPerPage = action.payload;
    },
    
    // Sorting actions
    setSortBy: (state, action) => {
      state.sortBy = action.payload;
    },
    setSortOrder: (state, action) => {
      state.sortOrder = action.payload;
    },
    
    // Hide mode actions
    toggleHideMode: (state) => {
      state.hideMode = !state.hideMode;
    },
    setHideMode: (state, action) => {
      state.hideMode = action.payload;
    },
    
    // Clear all UI state (for logout)
    clearUIState: (state) => {
      return { ...initialState };
    },
  },
});

// Export actions
export const {
  openCandidateDetail,
  closeCandidateDetail,
  setViewMode,
  toggleTheme,
  setTheme,
  addNotification,
  removeNotification,
  clearNotifications,
  setGlobalLoading,
  toggleSidebar,
  openSidebar,
  closeSidebar,
  setSidebarOpen,
  toggleSidebarExpanded,
  setSidebarExpanded,
  setMobileView,
  setSearchQuery,
  setActiveFilters,
  clearFilters,
  setCurrentPage,
  setItemsPerPage,
  setSortBy,
  setSortOrder,
  toggleHideMode,
  setHideMode,
  clearUIState,
} = uiSlice.actions;

// Export selectors
export const selectCandidateDetailDialog = (state) => state.ui.candidateDetailDialog;
export const selectViewMode = (state) => state.ui.viewMode;
export const selectTheme = (state) => state.ui.theme;
export const selectNotifications = (state) => state.ui.notifications;
export const selectGlobalLoading = (state) => state.ui.globalLoading;
export const selectSidebarOpen = (state) => state.ui.sidebarOpen;
export const selectSidebarExpanded = (state) => state.ui.sidebarExpanded;
export const selectIsMobile = (state) => state.ui.isMobile;
export const selectSearchQuery = (state) => state.ui.searchQuery;
export const selectActiveFilters = (state) => state.ui.activeFilters;
export const selectCurrentPage = (state) => state.ui.currentPage;
export const selectItemsPerPage = (state) => state.ui.itemsPerPage;
export const selectSortBy = (state) => state.ui.sortBy;
export const selectSortOrder = (state) => state.ui.sortOrder;
export const selectHideMode = (state) => state.ui.hideMode;

export default uiSlice.reducer; 